import { EnumToArrayValues } from '@vestico/utils';

import { SupportedMatchInfoHashCategory } from '../userMatchInfoHash';
import { ProductBase, ProductVariant, ProductVariantUID, ProductVariantsMap, SizeType, isApparelMatchingCategory } from './product';

export const sortVariants = (variants: ProductVariant[], filterSizeType?: SizeType) => {
  const sortedVariants = variants
    .filter((v) => filterSizeType === undefined || v.type === filterSizeType)
    .sort((a, b) => {
      const { type: aType, index: aIndex } = a;
      const { type: bType, index: bIndex } = b;

      if (aIndex < bIndex) {
        return -1;
      }
      if (aIndex > bIndex) {
        return 1;
      }
      if (aType || bType) {
        if (!bType || aType < bType) {
          return -1;
        }
        if (!aType || aType > bType) {
          return 1;
        }
      }

      return a.uid.localeCompare(b.uid);
    });
  return sortedVariants;
};

export const getVariants = (product: ProductBase, filterSizeType?: SizeType) => {
  const { variantUIDMap } = product;
  const variants = Object.values(variantUIDMap).flatMap((v) => v);
  return sortVariants(variants, filterSizeType);
};

export const getSizes = (product: ProductBase, toLowerCase?: boolean) => {
  const sizeTypes = EnumToArrayValues(SizeType);
  const noneSizeType = sizeTypes.shift();
  sizeTypes.push(noneSizeType);

  for (const sizeType of sizeTypes) {
    const allVariants = getVariants(product, sizeType);
    if (allVariants.length > 0) {
      const existingSizes = new Set();
      const filteredSizes: string[] = [];
      for (const variant of allVariants) {
        const size = variant.size?.trim();
        if (!size || size.length === 0 || existingSizes.has(size)) {
          continue;
        }
        filteredSizes.push(toLowerCase ? size.toLowerCase() : size);
        existingSizes.add(size);
      }
      return filteredSizes;
    }
  }
  return [];
};

const preferredOtherOptions = ['waist', 'length'];

export const getFullVariantString = (variant: ProductVariant, category?: SupportedMatchInfoHashCategory) => {
  const { size, otherOptions } = variant;
  const sanitizedOtherOptions = otherOptions
    ? Object.keys(otherOptions)
        .sort((a, b) => {
          for (const preferredOtherOption of preferredOtherOptions) {
            if (a === preferredOtherOption) {
              return -1;
            }
            if (b === preferredOtherOption) {
              return 1;
            }
          }
          return a.localeCompare(b);
        })
        .map((key) => {
          return otherOptions[key];
        })
    : [];

  const options = [size, ...sanitizedOtherOptions].filter((v) => !!v);
  if (options.length === 0 && isApparelMatchingCategory(category)) {
    return 'One Size';
  }
  return options.join(' / ');
};

export type ProductSizeVariantsMapping = Map<string, ProductVariant[]>;

export const createVariantKey = (productVariant: ProductVariant, useUpperCase?: boolean, byOtherAttributes?: string[]) => {
  const { size, otherOptions } = productVariant;
  if (!size || !otherOptions) {
    return undefined;
  }

  const sanitizedSize = useUpperCase ? size.toUpperCase() : size.toLowerCase();
  const otherOptionValues = byOtherAttributes ? byOtherAttributes.map((opt) => otherOptions[opt]?.toLowerCase()) : [];
  const key = [sanitizedSize, ...otherOptionValues].join('.');
  return key;
};

export const getProductVariantsBySize = (product: ProductBase, useUpperCase?: boolean, byOtherAttributes?: string[]) => {
  const productVariantsBySize: ProductSizeVariantsMapping = new Map();

  const productVariants = getVariants(product);

  for (const productVariant of productVariants) {
    const key = createVariantKey(productVariant, useUpperCase, byOtherAttributes);
    if (!key) {
      continue;
    }

    let sizeVariants = productVariantsBySize.get(key);
    if (!sizeVariants) {
      sizeVariants = [];
      productVariantsBySize.set(key, sizeVariants);
    }
    sizeVariants.push(productVariant);
  }

  return productVariantsBySize;
};

export const getProductVariantsForSizeOfVariant = (product: ProductBase, variantUID: ProductVariantUID, additionalProductAttributes: string[]) => {
  const { variantUIDMap } = product;
  const variants = variantUIDMap[variantUID];
  if (!variants) {
    return [];
  }

  const variant = variants[0];

  if (!variant) {
    return [];
  }

  const mainVariantSize = variant.size?.toLowerCase();
  if (!mainVariantSize) {
    return [];
  }

  const mainVariantOptions = [
    mainVariantSize,
    ...additionalProductAttributes.map((o) => variant.otherOptions[o.toLowerCase()]?.toLowerCase()).sort(),
  ];

  const matchingVariants: ProductVariant[] = [];
  const productVariants = getVariants(product);

  for (const productVariant of productVariants) {
    if (productVariant === variant) {
      continue;
    }

    const { size, otherOptions } = productVariant;
    if (!size) {
      continue;
    }

    const allOptions = [
      size,
      ...Object.keys(otherOptions)
        .filter((o) => additionalProductAttributes.includes(o.toLowerCase()))
        .map((option) => otherOptions[option]?.toLowerCase())
        .sort(),
    ];
    const matchingOptions = allOptions.filter((option, index) => option === mainVariantOptions[index]);
    if (matchingOptions.length === mainVariantOptions.length) {
      matchingVariants.push(productVariant);
    }
  }

  return matchingVariants;
};

export const getProductVariantsBySizeFilteredByOtherOptions = (product: ProductBase, variantUID: ProductVariantUID) => {
  const { variantUIDMap } = product;
  const variants = variantUIDMap[variantUID];
  if (!variants) {
    return undefined;
  }

  const variant = variants[0];

  if (!variant) {
    return undefined;
  }

  const { otherOptions } = variant;
  const mainVariantOtherOptionsIdentifier = Object.values(otherOptions).join('/').toLowerCase();

  const variantsBySize = getProductVariantsBySize(product);

  for (const [key, variants] of variantsBySize) {
    const filteredVariants = variants.filter((sizeVariant) => {
      const { otherOptions } = sizeVariant;
      const otherOptionsIdentifier = Object.values(otherOptions).join('/').toLowerCase();
      return otherOptionsIdentifier === mainVariantOtherOptionsIdentifier;
    });
    variantsBySize.set(key, filteredVariants);
  }

  return variantsBySize;
};

export const createVariantsMap = (variants: ProductVariant[], sort?: boolean) => {
  const sortedVariants: ProductVariant[] = sort ? sortVariants(variants) : variants;
  const variantUIDMap: ProductVariantsMap = {};
  for (let index = 0; index < sortedVariants.length; index++) {
    const variant = sortedVariants[index];
    const { uid } = variant;
    variant.index = index;
    let existingVariants = variantUIDMap[uid];
    if (!existingVariants) {
      existingVariants = [];
      variantUIDMap[uid] = existingVariants;
    }
    existingVariants.push(variant);
  }
  return variantUIDMap;
};

export const getMatchingVariantUIDForProduct = (
  product: ProductBase,
  variant: ProductVariant,
  additionalProductAttributes: string[]
): ProductVariant | undefined => {
  if (!variant) {
    return undefined;
  }

  for (const productVariants of Object.values(product.variantUIDMap)) {
    for (const productVariant of productVariants) {
      const { size, otherOptions } = productVariant;
      if (!size) {
        continue;
      }
      const isMatchingSize = size.trim().toLowerCase() === variant.size?.toLowerCase();
      if (isMatchingSize && additionalProductAttributes.length === 0) {
        return productVariant;
      }

      const matchingAttributes = additionalProductAttributes.filter((attribute) => {
        const productVariantAttr = otherOptions[attribute];
        const variantAttr = variant.otherOptions[attribute];
        return productVariantAttr?.trim().toLowerCase() === variantAttr?.trim().toLowerCase();
      });

      if (isMatchingSize && matchingAttributes.length === additionalProductAttributes.length) {
        return productVariant;
      }
    }
  }
  return undefined;
};
